import { z } from 'zod';
/**
 * The number ranges that may be maintained through the settings: the ones a
 * document number is written from. `invoice` deliberately does not create
 * itself — that range is configured on purpose and may continue a numbering
 * from the previous system.
 *
 * The `contact` range is not one of them. A contact number is a running whole
 * number, so that range has a next value and nothing else, and its own schema
 * below (#56).
 */
export const numberRangeCodes = ['invoice'];
export const numberRangeCodeSchema = z.enum(numberRangeCodes);
export const numberRangeInputSchema = z.object({
    /** Becomes part of a file name (`invoices/{year}/{number}.pdf`), so a slash
     *  or a space in here would be a path problem. */
    prefix: z
        .string()
        .trim()
        .max(20)
        .regex(/^[A-Za-z0-9._-]*$/)
        .default(''),
    padding: z.number().int().min(1).max(12).default(1),
    nextValue: z.number().int().min(1),
});
export const numberRangeSchema = numberRangeInputSchema.extend({
    id: z.uuid(),
    code: z.string(),
});
/**
 * How a counter value becomes a document number.
 *
 * Shared with the client so the settings screen can show what the next invoice
 * will be called before one exists. The result is frozen into `invoice.number`
 * at finalization — changing the padding later must never rewrite a number
 * that has already been issued.
 */
export function formatNumber(prefix, padding, value) {
    return `${prefix}${String(value).padStart(padding, '0')}`;
}
/** What an `integer` column holds — `contact.contact_number` is one. */
export const MAX_CONTACT_NUMBER = 2_147_483_647;
/**
 * The next contact number, as the settings maintain it ("Nächste
 * Kontaktnummer"). No prefix and no padding: `contact.contact_number` is a
 * whole number and nothing formats it (#56).
 */
export const contactNumberInputSchema = z.object({
    nextValue: z.number().int().min(1).max(MAX_CONTACT_NUMBER),
});
/** `nextValue` is null while no contact was ever created and nobody set it:
 *  the range creates itself with the first contact, starting at 1. */
export const contactNumberSchema = z.object({
    nextValue: z.number().int().min(1).nullable(),
});
//# sourceMappingURL=number-range.js.map